import { Vec3 } from './Vec3.js';
import { BoxCollider, StairRampCollider } from './Colliders.js';

/**
 * High-performance 3D Character Controller with continuous collision resolution,
 * curb stepping (0.35m), 4-story stair climbing, wall sliding, and gravity.
 */
export class CharacterController {
  constructor(options = {}) {
    this.physicsWorld = options.physicsWorld || null;

    // Dimensions
    this.radius = options.radius !== undefined ? options.radius : 0.35;
    this.height = options.height !== undefined ? options.height : 1.8;
    this.stepHeight = options.stepHeight !== undefined ? options.stepHeight : 0.35;

    // Movement parameters
    this.walkSpeed = options.walkSpeed !== undefined ? options.walkSpeed : 5.0;
    this.sprintMultiplier = options.sprintMultiplier !== undefined ? options.sprintMultiplier : 1.6; // 1.6x sprint
    this.jumpSpeed = options.jumpSpeed !== undefined ? options.jumpSpeed : 7.0;
    this.gravity = options.gravity !== undefined ? options.gravity : -18.0; // g = -18 m/s^2
    this.terminalVelocity = options.terminalVelocity !== undefined ? options.terminalVelocity : -40.0;
    this.maxWalkableSlope = options.maxWalkableSlope !== undefined ? options.maxWalkableSlope : 0.6; // cos(theta) ~ 53 deg

    // Physics state
    this.position = Vec3.from(options.position || { x: 0, y: 0, z: 0 }); // Feet position
    this.velocity = Vec3.from(options.velocity || { x: 0, y: 0, z: 0 });
    this.isGrounded = false;
    this.isSprinting = false;
    this.isJumping = false;
    this.groundNormal = new Vec3(0, 1, 0);
    this.groundCollider = null;

    // Input state buffer
    this.inputMove = new Vec3(0, 0, 0);
    this.inputJump = false;
    this.inputSprint = false;
    this.yaw = 0; // Rotation around Y axis in radians

    if (this.physicsWorld) {
      this.physicsWorld.addCharacter(this);
    }
  }

  setPhysicsWorld(world) {
    this.physicsWorld = world;
  }

  teleport(x, y, z) {
    if (typeof x === 'object') {
      this.position.set(x.x, x.y, x.z);
    } else {
      this.position.set(x, y, z);
    }
    this.velocity.set(0, 0, 0);
    this.isGrounded = false;
  }

  getCapsule(atPos = this.position) {
    const r = this.radius;
    const baseCenter = new Vec3(atPos.x, atPos.y + r, atPos.z);
    const topCenter = new Vec3(atPos.x, atPos.y + this.height - r, atPos.z);
    return { baseCenter, topCenter, radius: r };
  }

  setInput({ moveX = 0, moveZ = 0, jump = false, sprint = false, yaw = 0 }) {
    this.inputMove.set(moveX, 0, moveZ);
    this.inputJump = !!jump;
    this.inputSprint = !!sprint;
    this.yaw = yaw;
  }

  /**
   * Check collision against all colliders in world at a test position.
   */
  testCollisionAt(testPos) {
    if (!this.physicsWorld) return [];
    const capsule = this.getCapsule(testPos);
    const candidates = this.physicsWorld.queryCapsule(capsule.baseCenter, capsule.topCenter, capsule.radius);
    const hits = [];

    for (const col of candidates) {
      if (col.isTrigger) continue;
      const hit = col.testCapsule(capsule.baseCenter, capsule.topCenter, capsule.radius);
      if (hit && hit.penetration > 1e-5) {
        hits.push(hit);
      }
    }
    return hits;
  }

  /**
   * Resolve overlapping penetrations at current position.
   */
  depenetrate(currentPos, maxIterations = 4) {
    let groundedThisPass = false;
    let bestGroundNormal = new Vec3(0, 1, 0);
    let bestGroundCol = null;

    for (let iter = 0; iter < maxIterations; iter++) {
      const hits = this.testCollisionAt(currentPos);
      if (hits.length === 0) break;

      hits.sort((a, b) => b.penetration - a.penetration);

      for (const hit of hits) {
        const capsule = this.getCapsule(currentPos);
        const freshHit = hit.collider.testCapsule(capsule.baseCenter, capsule.topCenter, capsule.radius);
        if (!freshHit || freshHit.penetration <= 1e-5) continue;

        currentPos.addScaledVector(freshHit.normal, freshHit.penetration);

        if (freshHit.normal.y >= this.maxWalkableSlope) {
          groundedThisPass = true;
          bestGroundNormal.copy(freshHit.normal);
          bestGroundCol = freshHit.collider;
          if (this.velocity.y < 0) {
            this.velocity.y = 0;
          }
        } else if (freshHit.normal.y <= -this.maxWalkableSlope) {
          if (this.velocity.y > 0) {
            this.velocity.y = 0;
          }
        }
      }
    }

    return { grounded: groundedThisPass, groundNormal: bestGroundNormal, groundCollider: bestGroundCol };
  }

  /**
   * Check if character is standing on a walkable ground surface.
   */
  checkGrounding(currentPos) {
    if (!this.physicsWorld) return { grounded: false, normal: new Vec3(0, 1, 0), collider: null };

    // 1. Direct contact depenetration
    const depen = this.depenetrate(currentPos);
    if (depen.grounded) {
      return depen;
    }

    // 2. Downward ground probe only when not jumping and velocity.y <= 0
    if (this.velocity.y <= 0 && !this.inputJump) {
      const probeOrigin = new Vec3(currentPos.x, currentPos.y + 0.2, currentPos.z);
      const hit = this.physicsWorld.raycast(probeOrigin, new Vec3(0, -1, 0), 0.45, col => !col.isTrigger);

      if (hit && hit.normal.y >= this.maxWalkableSlope) {
        currentPos.y = hit.point.y;
        return { grounded: true, groundNormal: hit.normal, groundCollider: hit.collider };
      }
    }

    return { grounded: false, groundNormal: new Vec3(0, 1, 0), collider: null };
  }

  /**
   * Move capsule with continuous sweep and wall sliding.
   */
  sweepMove(currentPos, moveDelta) {
    let remainingDelta = moveDelta.clone();
    let iter = 0;
    const maxIters = 4;

    while (remainingDelta.lengthSq() > 1e-8 && iter < maxIters) {
      iter++;
      const stepLen = remainingDelta.length();
      const numSubSteps = Math.max(1, Math.min(10, Math.ceil(stepLen / 0.02)));
      const subStepDelta = new Vec3().copy(remainingDelta).divideScalar(numSubSteps);

      let collided = false;
      let hitNormal = new Vec3();

      for (let s = 0; s < numSubSteps; s++) {
        const nextPos = new Vec3().addVectors(currentPos, subStepDelta);
        const hits = this.testCollisionAt(nextPos);

        if (hits.length > 0) {
          collided = true;
          hits.sort((a, b) => b.penetration - a.penetration);
          hitNormal.copy(hits[0].normal);

          currentPos.copy(nextPos);
          this.depenetrate(currentPos);
          break;
        } else {
          currentPos.copy(nextPos);
        }
      }

      if (collided) {
        const dot = remainingDelta.dot(hitNormal);
        if (dot < 0) {
          remainingDelta.x -= hitNormal.x * dot;
          remainingDelta.y -= hitNormal.y * dot;
          remainingDelta.z -= hitNormal.z * dot;
        }
      } else {
        break;
      }
    }
  }

  /**
   * Attempt step-up movement over low curbs/steps (<= stepHeight).
   */
  tryStepMove(currentPos, horizDelta) {
    if (horizDelta.lengthSq() < 1e-8) return false;

    const directPos = new Vec3().addVectors(currentPos, horizDelta);
    const directHits = this.testCollisionAt(directPos);

    if (directHits.length === 0) {
      currentPos.copy(directPos);
      return true;
    }

    const candidateElevations = new Set();

    // Check hit colliders for walkable step surfaces
    for (const hit of directHits) {
      const col = hit.collider;
      if (col instanceof BoxCollider) {
        const topY = col.center.y + col.halfExtents.y;
        if (topY >= currentPos.y - 0.05 && topY - currentPos.y <= this.stepHeight + 0.02) {
          candidateElevations.add(topY);
        }
      } else if (col instanceof StairRampCollider) {
        const h = col.getSurfaceHeightAt(directPos.x, directPos.z);
        if (h >= currentPos.y - 0.05 && h - currentPos.y <= this.stepHeight + 0.02) {
          candidateElevations.add(h);
        }
      }
    }

    // Downward raycast probe
    if (this.physicsWorld) {
      const probeOrigin = new Vec3(directPos.x, currentPos.y + this.stepHeight + 0.1, directPos.z);
      const rayHit = this.physicsWorld.raycast(probeOrigin, new Vec3(0, -1, 0), this.stepHeight + 0.3, col => !col.isTrigger);

      if (rayHit && rayHit.normal.y >= this.maxWalkableSlope) {
        if (rayHit.point.y >= currentPos.y - 0.05 && rayHit.point.y - currentPos.y <= this.stepHeight + 0.02) {
          candidateElevations.add(rayHit.point.y);
        }
      }
    }

    if (candidateElevations.size === 0) {
      return false;
    }

    const elevList = Array.from(candidateElevations).sort((a, b) => b - a);

    for (const elev of elevList) {
      const testPos = new Vec3(directPos.x, elev, directPos.z);
      const testHits = this.testCollisionAt(testPos);
      const hasWallOrCeil = testHits.some(h => h.normal.y < this.maxWalkableSlope);

      if (!hasWallOrCeil) {
        currentPos.copy(testPos);
        return true;
      }
    }

    return false;
  }

  /**
   * Fixed 60Hz Physics Update step.
   */
  fixedUpdate(dt) {
    // 1. Calculate desired horizontal movement direction
    let moveDir = new Vec3(0, 0, 0);
    const inputLenSq = this.inputMove.x * this.inputMove.x + this.inputMove.z * this.inputMove.z;

    if (inputLenSq > 1e-4) {
      const inputLen = Math.sqrt(inputLenSq);
      const normX = this.inputMove.x / inputLen;
      const normZ = this.inputMove.z / inputLen;

      const cosY = Math.cos(this.yaw);
      const sinY = Math.sin(this.yaw);
      moveDir.x = normX * cosY + normZ * sinY;
      moveDir.z = -normX * sinY + normZ * cosY;
    }

    // Speed calculation (walk vs 1.6x sprint)
    this.isSprinting = this.inputSprint;
    const currentSpeed = this.walkSpeed * (this.isSprinting ? this.sprintMultiplier : 1.0);

    // 2. Handle Jump
    if (this.inputJump && this.isGrounded) {
      this.velocity.y = this.jumpSpeed;
      this.isGrounded = false;
      this.isJumping = true;
    }

    // 3. Apply Gravity
    if (!this.isGrounded) {
      this.velocity.y += this.gravity * dt;
      if (this.velocity.y < this.terminalVelocity) {
        this.velocity.y = this.terminalVelocity;
      }
    }

    // 4. Horizontal velocity update
    let targetHorizVel = new Vec3(moveDir.x * currentSpeed, 0, moveDir.z * currentSpeed);

    // If on a walkable slope / stair ramp, align movement along slope tangent for smooth climbing
    if (this.isGrounded && this.groundNormal.y < 0.999 && this.groundNormal.y >= this.maxWalkableSlope) {
      const n = this.groundNormal;
      const v = targetHorizVel;
      const vDotN = v.dot(n);
      targetHorizVel.set(v.x - n.x * vDotN, -n.y * vDotN, v.z - n.z * vDotN);
    }

    const accelRate = this.isGrounded ? 25.0 : 8.0;
    this.velocity.x += (targetHorizVel.x - this.velocity.x) * Math.min(1.0, accelRate * dt);
    this.velocity.z += (targetHorizVel.z - this.velocity.z) * Math.min(1.0, accelRate * dt);

    // 5. Execute Movement with CCD & Step-height handling
    const horizDelta = new Vec3(this.velocity.x * dt, 0, this.velocity.z * dt);
    const vertDelta = new Vec3(0, (targetHorizVel.y !== 0 && this.isGrounded ? targetHorizVel.y * dt : this.velocity.y * dt), 0);

    const newPos = this.position.clone();

    // Try curb step-up first if grounded and moving horizontally
    let stepped = false;
    if (this.isGrounded && horizDelta.lengthSq() > 1e-8) {
      stepped = this.tryStepMove(newPos, horizDelta);
    }

    if (!stepped && horizDelta.lengthSq() > 1e-8) {
      this.sweepMove(newPos, horizDelta);
    }

    // Perform vertical movement only if not stepped
    if (!stepped && vertDelta.lengthSq() > 1e-8) {
      newPos.add(vertDelta);
    }

    // 6. Ground snapping when walking down ramps/stairs (only if not stepping up)
    if (!stepped && this.isGrounded && this.velocity.y <= 0 && !this.inputJump) {
      const snapDist = 0.45;
      const snapOrigin = new Vec3(newPos.x, newPos.y + 0.1, newPos.z);
      const groundHit = this.physicsWorld ? this.physicsWorld.raycast(
        snapOrigin,
        new Vec3(0, -1, 0),
        snapDist + 0.1,
        col => !col.isTrigger
      ) : null;

      if (groundHit && groundHit.normal.y >= this.maxWalkableSlope) {
        newPos.y = groundHit.point.y;
      }
    }

    // 7. Depenetration & Final Ground State Resolution
    const groundInfo = this.checkGrounding(newPos);
    this.position.copy(newPos);
    this.isGrounded = groundInfo.grounded;
    this.groundNormal.copy(groundInfo.groundNormal);
    this.groundCollider = groundInfo.groundCollider || null;

    if (this.isGrounded && this.velocity.y < 0) {
      this.velocity.y = 0;
      this.isJumping = false;
    }
  }

  update(dt, inputState = null) {
    if (inputState) {
      this.setInput(inputState);
    }
    if (!this.physicsWorld) {
      this.fixedUpdate(dt);
    }
  }
}
